// case-studies/pages/taxgenie.js
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import "../styles/taxgenie.css";

import example1 from "../assets/taxgenie/taxgeniebg.png";
import example2 from "../assets/taxgenie/deloittedubai.jpg";
import example3 from "../assets/taxgenie/scraping.png";
import example4 from "../assets/taxgenie/ocr.jpg";
import example5 from "../assets/taxgenie/taxgenieaward.jpg";
import example6 from "../assets/taxgenie/deloittefooter.jpg";

const titleVariant = {
  hidden: { opacity: 0, y: -28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const textVariant = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 1.18 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const TaxGenie = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  return (
    <main className="tg-root">
      {/* HEADER */}
      <header className="tg-header">
        <div className="tg-header-inner">
          <div className="tg-title-block">
            <motion.h1
              className="tg-title"
              variants={titleVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              DELOITTE: TAX GENIE
            </motion.h1>

            <motion.p
              className="tg-subtitle"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              How quitting a startup, dodging a contract and wrestling with
              unreadable PDFs led to an award winning GenAI project.
            </motion.p>
          </div>

          <div className="tg-meta-row">
            <motion.div
              className="tg-meta-line"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
            <motion.div
              className="tg-meta-items"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <div className="tg-meta-item">
                <span className="tg-meta-label">Project Type</span>
                <span className="tg-meta-value">GenAI implementation</span>
              </div>
              <div className="tg-meta-item">
                <span className="tg-meta-label">Role</span>
                <span className="tg-meta-value">
                  AI developer and prototype lead
                </span>
              </div>
              <div className="tg-meta-item">
                <span className="tg-meta-label">Year</span>
                <span className="tg-meta-value">2023</span>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* BANNER */}
      <section className="tg-banner">
        <div className="tg-banner-inner">
          <motion.img
            src={example1}
            alt="Tax Genie banner"
            className="tg-banner-image"
            variants={imageVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          />

          <motion.a
            href="https://www.deloitte.com/middle-east/en/about/press-room/dme-tax-genie-2-recognized-for-innovation-in-tax-and-legal-services.html"
            className="tg-view-link"
            target="_blank"
            rel="noopener noreferrer"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            VIEW PRESS RELEASE
          </motion.a>
        </div>
      </section>

      {/* 1. HOOK */}
      <section className="tg-section tg-section-full">
        <div className="tg-section-inner">
          <motion.h2
            className="tg-section-title tg-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            The Hook
          </motion.h2>

          <motion.div
            className="tg-section-body tg-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              Fresh out of uni, four months into a tech startup, and enjoying
              the incredible privilege of{" "}
              <span className="tg-emphasis-inline">
                paying for my own parking every day
              </span>
              , I had a moment of clarity.
            </p>
            <p>
              This was not it. Not the place. Not the project. Not the future.
              The company felt like it was held together with good intentions
              and a Github repository. I knew I had to leave before the
              building collapsed and I got blamed for holding the wrong pillar.
            </p>
            <p>
              So I started applying. Four offers came in. The one I really
              wanted was a{" "}
              <span className="tg-emphasis-inline">
                summer internship at Deloitte
              </span>
              . The problem was simple.{" "}
              <span className="tg-emphasis-inline">
                Impostor syndrome had me in a chokehold
              </span>
              . I did not think I had enough proof of anything yet, so I
              accepted a safer offer from a travel agency.
            </p>
            <p>
              Five rounds of interviews. Data analyst role. Get hotel prices
              right. Nice. Predictable. Simple. The contract hit my inbox and at
              that exact moment,{" "}
              <span className="tg-emphasis-inline">
                Deloitte called and said they wanted me
              </span>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. CONTEXT – title, image, text */}
      <section className="tg-section tg-section-split tg-split-text-left">
        <div className="tg-section-inner tg-section-inner-split">
          <motion.h3
            className="tg-section-title tg-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Context
          </motion.h3>

          <div className="tg-split-col tg-split-col-media">
            <div className="tg-image-frame">
              <motion.img
                src={example2}
                alt="Deloitte context"
                className="tg-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="tg-split-col tg-split-col-text">
            <motion.div
              className="tg-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                I walked into Deloitte expecting the usual corporate maze.
                Instead, I got honesty. Real honesty. There was a project the
                Tax and Legal team approved months ago. The person leading it
                ignored it and it was quietly dying.
              </p>
              <p>
                No corporate spin. No buzzword salad. Just a simple request:{" "}
                <span className="tg-emphasis-inline">
                  this thing is falling apart, please fix it
                </span>
                .
              </p>
              <p>
                They handed it to the Digital Foundry team and two interns. Me,
                and technically another person. And I say technically because
                the other intern was battling final year coursework like it was
                a full time job. Half the time they looked like they were trying
                to remember who they were.
              </p>
              <p>
                Smart person, but completely cooked by exams. Some days they
                helped, most days they drifted into another dimension. So
                naturally the bulk of the workload fell to me. Not because
                anyone forced it. Someone just had to{" "}
                <span className="tg-emphasis-inline">
                  keep the project from face planting
                </span>
                .
              </p>
              <p>
                The name of the project was{" "}
                <span className="tg-green-emphasis-inline">Tax Genie</span>. The
                idea was brilliant. An internal assistant that could answer
                anything related to Tax and Legal. Great concept. Terrible
                situation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. CHALLENGE – title, image, text */}
      <section className="tg-section tg-section-split tg-split-text-right">
        <div className="tg-section-inner tg-section-inner-split">
          <motion.h3
            className="tg-section-title tg-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Challenge
          </motion.h3>

          <div className="tg-split-col tg-split-col-media">
            <div className="tg-image-frame">
              <motion.img
                src={example3}
                alt="PDF challenge"
                className="tg-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="tg-split-col tg-split-col-text">
            <motion.div
              className="tg-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                Our first idea was the obvious one. Use RAG. Use LangChain,
                build a prompt template, embed the documents and let the model
                do the heavy lifting.
              </p>
              <p>
                Except every bit of data we had was in PDFs. Not normal PDFs.
                Not text layers. Just{" "}
                <span className="tg-emphasis-inline">images of text</span>.
                Completely unreadable without OCR. And even OCR was waving a
                white flag.
              </p>
              <p>
                Our simple prototype turned into a survival mission. Sleepless
                nights. Working weekends. A partner from the Tax and Legal wing
                sending messages that slowly drained the life out of my manager.
              </p>
              <p>
                Meanwhile the other intern was switching between helping debug
                and trying not to fail their degree. Respectable effort, but the
                workload was definitely not fifty fifty. So I kept grinding
                until the prototype worked. We shipped it, with the RAG database
                still cooking, and people loved it. We actually saved the
                project.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. STRATEGY */}
      <section className="tg-section tg-section-full">
        <div className="tg-section-inner">
          <motion.h2
            className="tg-section-title tg-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Strategy
          </motion.h2>

          <motion.div
            className="tg-section-body tg-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              Deloitte taught me a lesson very fast.{" "}
              <span className="tg-emphasis-inline">Speed wins</span>. Not the
              speed of coding. The speed of deciding.
              In big firms, one missing signature, one person on leave, one
              email stuck in a draft folder, one casual “let us align next
              week” can blow up an entire timeline.
            </p>
            <p>
              We had no perfect data. No final architecture. Barely any
              documentation. And two interns. But we had momentum.{" "}
              <span className="tg-emphasis-inline">
                Momentum beats perfection every time
              </span>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. BUILD – title, image, text */}
      <section className="tg-section tg-section-split tg-split-text-left">
        <div className="tg-section-inner tg-section-inner-split">
          <motion.h3
            className="tg-section-title tg-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Build
          </motion.h3>

          <div className="tg-split-col tg-split-col-media">
            <div className="tg-image-frame">
              <motion.img
                src={example4}
                alt="Tax Genie build"
                className="tg-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="tg-split-col tg-split-col-text">
            <motion.div
              className="tg-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                Our prototype looked clean on the outside, but behind the
                curtain it was held together by OCR pipelines begging for mercy,
                PDFs that should be illegal, half structured legal text,
                LangChain templates patched with hope, a RAG system warming up
                in the background, an overworked manager hanging on,
                and me trying to make sure the whole thing did not collapse like
                a Jenga tower.
              </p>
              <p>
                Somehow it worked. Somehow it shipped. Somehow it made sense. I
                still do not know how.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. OUTCOME – title, image, text */}
      <section className="tg-section tg-section-split tg-split-text-right">
        <div className="tg-section-inner tg-section-inner-split">
          <motion.h3
            className="tg-section-title tg-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Outcome
          </motion.h3>

          <div className="tg-split-col tg-split-col-media">
            <div className="tg-image-frame">
              <motion.img
                src={example5}
                alt="Tax Genie outcome"
                className="tg-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="tg-split-col tg-split-col-text">
            <motion.div
              className="tg-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                A year later, I was not even at the firm anymore and Deloitte
                released Tax Genie 2.0. And the crazy part is that it{" "}
                <span className="tg-emphasis-inline">started winning awards</span>
                .
              </p>
              <p>
                BIG Innovation Award. Gold Stevie Award. Silver Stevie Award.
                All for the same platform that started with two interns, a
                neglected project, unreadable PDFs, OCR duct taped together, and
                a timeline that should not have been possible.
              </p>
              <p>
                Seeing that updated version get global recognition was surreal.
                I was not there to witness it evolve, but the groundwork we
                built clearly became the system’s backbone.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. REFLECTION */}
      <section className="tg-section tg-section-full tg-section-reflection">
        <div className="tg-section-inner">
          <motion.h2
            className="tg-section-title tg-section-title-left"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Reflection
          </motion.h2>

          <motion.div
            className="tg-section-body tg-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              Tax Genie taught me more in a few months than most roles teach in
              a year. I learned how dangerous slow decision making is and how
              far responsibility can go when given to someone who genuinely
              wants to build.
            </p>
            <p>
              I learned that great projects rarely begin with perfect
              conditions. Sometimes they start with unreadable PDFs and an
              intern pretending they know what they are doing.
              And the funniest part is that the entire story happened because I
              did not want to keep paying for parking at a startup.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CLOSING VISUAL */}
      <section className="tg-section tg-closing-visual">
        <div className="tg-section-inner">
          <div className="tg-image-frame tg-image-frame-soft">
            <motion.img
              src={example6}
              alt="Closing visual"
              className="tg-image"
              variants={imageVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default TaxGenie;
